/**
 * AppDataContext — global cache for data that is:
 *   1. Shared across multiple pages (gyms, exercises, food items)
 *   2. Rarely changes during a session (premium status)
 *   3. Expensive to re-fetch on every page mount
 *
 * Usage:
 *   const { gyms, exercises, foodItems, premiumStatus, reloadGyms } = useAppData();
 *
 * All data is fetched once when the user is authenticated and cached for the
 * lifetime of the session. Individual pages can call reload* functions to
 * force a refresh after mutations (e.g. after creating a gym).
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { gymsAPI, exercisesAPI, foodAPI, premiumAPI } from '../services/api';

const AppDataContext = createContext(null);

export function AppDataProvider({ children }) {
  // ── Gyms ──────────────────────────────────────────────────────────────────
  const [gyms, setGyms]           = useState([]);
  const [gymsLoading, setGymsLoading] = useState(false);
  const gymsFetched = useRef(false);

  // ── Exercises (for plan builder) ──────────────────────────────────────────
  const [exercises, setExercises]         = useState([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const exercisesFetched = useRef(false);

  // ── Food items (for meal plan builder) ───────────────────────────────────
  const [foodItems, setFoodItems]         = useState([]);
  const [foodLoading, setFoodLoading]     = useState(false);
  const foodFetched = useRef(false);

  // ── Premium status ────────────────────────────────────────────────────────
  const [premiumStatus, setPremiumStatus]     = useState(null);
  const [premiumLoading, setPremiumLoading]   = useState(false);
  const premiumFetched = useRef(false);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const isLoggedIn = () => !!localStorage.getItem('token');

  // ── Fetch functions (idempotent — skip if already fetched) ────────────────

  const loadGyms = useCallback(async (force = false) => {
    if (!isLoggedIn()) return;
    if (gymsFetched.current && !force) return;
    setGymsLoading(true);
    try {
      if (gymsAPI?.getAll) {
        const res = await gymsAPI.getAll();
        setGyms(res.data?.data || []);
      }
      gymsFetched.current = true;
    } catch (e) {
      console.error('[AppData] loadGyms error:', e.message);
    } finally {
      setGymsLoading(false);
    }
  }, []);

  const loadExercises = useCallback(async (force = false) => {
    if (!isLoggedIn()) return;
    if (exercisesFetched.current && !force) return;
    setExercisesLoading(true);
    try {
      if (exercisesAPI?.getAll) {
        const res = await exercisesAPI.getAll({ limit: 300, admin: 'true' });
        setExercises(res.data?.data || []);
      }
      exercisesFetched.current = true;
    } catch (e) {
      console.error('[AppData] loadExercises error:', e.message);
    } finally {
      setExercisesLoading(false);
    }
  }, []);

  const loadFoodItems = useCallback(async (force = false) => {
    if (!isLoggedIn()) return;
    if (foodFetched.current && !force) return;
    setFoodLoading(true);
    try {
      if (foodAPI?.getAll) {
        const res = await foodAPI.getAll({ limit: 500 });
        setFoodItems(res.data?.data || []);
      }
      foodFetched.current = true;
    } catch (e) {
      console.error('[AppData] loadFoodItems error:', e.message);
    } finally {
      setFoodLoading(false);
    }
  }, []);

  const loadPremiumStatus = useCallback(async (force = false) => {
    if (!isLoggedIn()) return;
    if (premiumFetched.current && !force) return;
    setPremiumLoading(true);
    try {
      if (premiumAPI?.getStatus) {
        const res = await premiumAPI.getStatus();
        setPremiumStatus(res.data?.data || res.data || null);
      }
      premiumFetched.current = true;
    } catch (e) {
      console.error('[AppData] loadPremiumStatus error:', e.message);
      setPremiumStatus({ isPremium: false });
      premiumFetched.current = true;
    } finally {
      setPremiumLoading(false);
    }
  }, []);

  // ── Public reload functions (force = true) ────────────────────────────────
  const reloadGyms     = useCallback(() => loadGyms(true),     [loadGyms]);
  const reloadExercises = useCallback(() => loadExercises(true), [loadExercises]);
  const reloadFoodItems = useCallback(() => loadFoodItems(true), [loadFoodItems]);
  const reloadPremium  = useCallback(() => loadPremiumStatus(true), [loadPremiumStatus]);

  // ── Reset on logout ───────────────────────────────────────────────────────
  const reset = useCallback(() => {
    setGyms([]);
    setExercises([]);
    setFoodItems([]);
    setPremiumStatus(null);
    gymsFetched.current      = false;
    exercisesFetched.current = false;
    foodFetched.current      = false;
    premiumFetched.current   = false;
  }, []);

  // ── Bootstrap: load gyms + premium on mount if logged in ─────────────────
  useEffect(() => {
    const refreshAccess = () => { if (isLoggedIn()) loadGyms(true); };
    window.addEventListener('focus', refreshAccess);
    return () => window.removeEventListener('focus', refreshAccess);
  }, [loadGyms]);

  // Exercises and food are lazy-loaded when their pages first need them.
  useEffect(() => {
    if (isLoggedIn()) {
      loadGyms();
      loadPremiumStatus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppDataContext.Provider value={{
      // Gyms
      gyms, gymsLoading, reloadGyms,
      // Exercises
      exercises, exercisesLoading, loadExercises, reloadExercises,
      // Food items
      foodItems, foodLoading, loadFoodItems, reloadFoodItems,
      // Premium
      premiumStatus, premiumLoading, reloadPremium,
      // Utilities
      reset,
    }}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) {
    return {
      gyms: [],
      gymsLoading: false,
      reloadGyms: () => {},
      exercises: [],
      exercisesLoading: false,
      loadExercises: () => {},
      reloadExercises: () => {},
      premiumStatus: null,
      premiumLoading: false,
      reloadPremium: () => {},
      reset: () => {},
    };
  }
  return ctx;
}
